import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PropTypes from 'prop-types';

import './email-verification.css';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { emailVerificationSchema } from '../../../../shared/validations/auth';
import { emailSchema } from '../../../../shared/validations/common';
import { resendVerification } from '../../../../shared/actions/authActions';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../../../shared/components/form";
import {
  EmailIcon,
  ClockIcon,
  HelpIcon,
  MailCheckIcon,
  UserCheckIcon,
  EditIcon,
  SupportIcon
} from '../../../../assets/Icons/SVG';
import HeaderSection from '../../../../components/layout/header';
import { ROLE } from "../../../../shared/constants/common";

// Constants
const COUNTDOWN_DURATION = 60;
const SUCCESS_MESSAGE_DURATION = 5000;

const ModernEmailVerification = ({ onBack }) => {
  const navigate = useNavigation();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState('');
  const [tempEmail, setTempEmail] = useState('');


  // State management
  const [uiState, setUiState] = useState({
    countdown: COUNTDOWN_DURATION,
    canResend: true,
    showSuccess: false,
    isLoading: false,
    isEditingEmail: false
  });

  // Initialize form with react-hook-form
  const form = useForm({
    resolver: zodResolver(emailVerificationSchema),
    defaultValues: {
      email: '',
      action: ''
    },
    mode: "onChange",
  });

  useEffect(() => {
    // Get email from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const emailFromUrl = urlParams.get('email');
    setUserEmail(emailFromUrl);

    if (emailFromUrl) {
      form.setValue('email', emailFromUrl);
    }
  }, [form, location.search]);

  useEffect(() => {
    if (uiState.countdown > 0) {
      const timer = setTimeout(() =>
        setUiState(prev => ({ ...prev, countdown: prev.countdown - 1 })), 1000);
      return () => clearTimeout(timer);
    } else {
      setUiState(prev => ({ ...prev, canResend: true }));
    }
  }, [uiState.countdown]);

  const progressWidth = uiState.canResend ? 0 : ((COUNTDOWN_DURATION - uiState.countdown) / COUNTDOWN_DURATION) * 100;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate.toLogin();
    }
  };

  const handleLogin = useCallback(() => {
    form.setValue('action', 'login');
    navigate.toLogin();
  }, [form, navigate]);

  const handleEditEmail = useCallback(() => {
    const currentEmail = form.getValues('email');
    setTempEmail(currentEmail);
    setUiState(prev => ({ ...prev, isEditingEmail: true }));
  }, [form]);

  const handleSaveEmail = useCallback(() => {
    const emailValue = tempEmail.trim();
    const currentEmail = form.watch('email');

    const isValidEmail = emailSchema.safeParse(emailValue).success;
    if (!isValidEmail) {
      return;
    }

    if (emailValue && emailValue !== currentEmail) {
      form.setValue('email', emailValue, { shouldValidate: true, shouldDirty: true });
    }
    setUiState(prev => ({ ...prev, isEditingEmail: false }));
  }, [tempEmail, form]);

  const handleCancelEdit = useCallback(() => {
    setTempEmail('');
    setUiState(prev => ({ ...prev, isEditingEmail: false }));
  }, []);

  const handleContactSupport = useCallback(() => {
    form.setValue('action', 'contact');
    navigate.toContactSupport();
  }, [form, navigate]);

  const handleResendEmail = useCallback(async () => {
    if (!uiState.canResend || uiState.isLoading) return;
    try {
      const formValues = form.getValues();
      const isUpdateEmail = userEmail !== formValues.email;
      const payload = {
        isUpdateEmail: isUpdateEmail,
        email: formValues.email,
        oldEmail: isUpdateEmail ? userEmail : ''
      };

      setUiState(prev => ({ ...prev, isLoading: true }));
      form.setValue('action', 'resend');

      const result = await resendVerification(payload);
      if (result.success) {
        // When user updates email and resend succeeds, treat the new email as
        // the "current" one so a later change (e.g. lee200 → lee300) sends
        // oldEmail = lee200, not the original lee100.
        if (isUpdateEmail) {
          setUserEmail(formValues.email);
        }
        setUiState(prev => ({
          ...prev,
          showSuccess: true,
          canResend: false,
          countdown: COUNTDOWN_DURATION
        }));

        const roleNumber = parseInt(result?.data?.rol, 10);
        if (roleNumber === ROLE.CREATOR_SUB || roleNumber === ROLE.CREATOR_CONTENT) {
          navigate.toUnderReview();
        } else {
          toast.success(result?.data?.message);
        }

        // Hide success message after specified duration
        setTimeout(() => {
          setUiState(prev => ({ ...prev, showSuccess: false }));
        }, SUCCESS_MESSAGE_DURATION);
      } else {
        throw new Error(result?.error || 'Failed to resend verification email');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to resend verification email';
      toast.error(errorMessage);
    } finally {
      setUiState(prev => ({ ...prev, isLoading: false }));
    }
  });

  return (
    <div className="email-verification-screen">
      <ToastContainer />

      <HeaderSection
        type="text"
        title="Verification"
        handleBack={handleBack}
      />

      {/* Animated Background Elements */}
      <div className="email-verification-bg">
        <div className="email-verification-bg-blur-1"></div>
        <div className="email-verification-bg-blur-2"></div>
        <div className="email-verification-bg-blur-3"></div>
      </div>

      <main className="email-verification-main">
        <Form {...form}>
          <div className="email-verification-container">
            <div className="email-verification-welcome">
              <div className="text-center">
                <div className="email-verification-icon">
                  <EmailIcon />
                </div>
                <h2 className="email-verification-heading">Verification Email</h2>
              </div>
              <p className="email-verification-description">
                Verification email sent 🎊 Check your inbox and click the link to confirm your account.
              </p>
            </div>

            {/* Email Not Received Section */}
            <div className="email-verification-not-received">
              <h3 className="email-verification-not-received-title">Didn't receive the email?</h3>
              <p className="email-verification-not-received-text">
                If you haven't received the verification email, please check your spam folder or try the following options:
              </p>

              <div className="email-verification-options">
                {/* Email Address Display */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="email-verification-email-display">
                      <FormLabel className="email-verification-email-label">Email sent to:</FormLabel>
                      <FormControl>
                        <div className="email-verification-email-container">
                          {uiState.isEditingEmail ? (
                            <div className="email-verification-email-edit">
                              <input
                                type="email"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                value={tempEmail}
                                onChange={(e) => setTempEmail(e.target.value)}
                                className="email-verification-email-input"
                                placeholder="Enter your email address"
                                autoFocus
                              />
                              <div className="email-verification-email-actions">
                                <button
                                  onClick={handleSaveEmail}
                                  className="email-verification-save-btn"
                                  type="button"
                                  disabled={!tempEmail.trim()}
                                >
                                  <i className="fa-solid fa-check"></i>
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="email-verification-cancel-btn"
                                  type="button"
                                >
                                  <i className="fa-solid fa-times"></i>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="email-verification-email-info">
                              <p className="email-verification-email-address">{form.watch('email')}</p>
                              <button
                                onClick={handleEditEmail}
                                className="email-verification-edit-btn"
                                type="button"
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                            </div>
                          )}
                          {/* <div className="email-verification-email-time">
                          <ClockIcon />
                          <span>Sent 2 minutes ago</span>
                        </div> */}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Resend Email Button */}
                <button
                  onClick={handleResendEmail}
                  disabled={!uiState.canResend || uiState.isLoading}
                  className={`email-verification-resend-btn ${(!uiState.canResend || uiState.isLoading) ? 'disabled' : ''}`}
                  type="button"
                >
                  <span className="email-verification-resend-text">
                    {uiState.isLoading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane"></i>
                        Resend Verification Email
                      </>
                    )}
                  </span>
                  {!uiState.isLoading && <div className="email-verification-resend-shimmer"></div>}
                </button>

                {/* Login Button */}
                <button
                  onClick={handleLogin}
                  className="email-verification-login-btn"
                  type="button"
                >
                  <span className="email-verification-login-text">
                    <i className="fa-solid fa-sign-in-alt"></i>
                    Login
                  </span>
                </button>

                {/* Success Message */}
                {uiState.showSuccess && (
                  <div className="email-verification-success">
                    <div className="email-verification-success-content">
                      <div className="email-verification-success-icon">
                        <i className="fa-solid fa-circle-check"></i>
                      </div>
                      <div className="email-verification-success-text">
                        <p className="email-verification-success-title">Verification email resent successfully!</p>
                        <p className="email-verification-success-subtitle">Please check your inbox</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Countdown Timer for Resend */}
            {!uiState.canResend && (
              <div className="email-verification-countdown">
                <p className="email-verification-countdown-text">
                  You can resend another email in <span className="email-verification-countdown-timer">{uiState.countdown}</span> seconds
                </p>
                <div className="email-verification-progress-container">
                  <div
                    className="email-verification-progress-bar"
                    style={{ width: `${progressWidth}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Additional Help Section */}
            <div className="email-verification-help mt-4">
              <div className="email-verification-help-item">
                <div className="email-verification-help-header">
                  <HelpIcon />
                  <h3>How long should I wait?</h3>
                </div>
                <p className="email-verification-help-text">
                  Verification emails typically arrive within 5 minutes. If you haven't received it after 15 minutes, try resending or check your spam folder.
                </p>
              </div>

              <div className="email-verification-help-item">
                <div className="email-verification-help-header">
                  <div className='mail_green_check_icon'>
                  <i className='mgci_1 fa-solid fa-envelope'></i>
                  <i className='mgci_2 fa-solid fa-circle-check'></i>
                  </div>
                  <h3>Check your spam folder</h3>
                </div>
                <p className="email-verification-help-text">
                  Our emails might be filtered as spam. Please check your spam or junk folder and mark our emails as "not spam".
                </p>
              </div>

              <div className="email-verification-help-item">
                <div className="email-verification-help-header">
                  <UserCheckIcon />
                  <h3>Verify your email address</h3>
                </div>
                <p className="email-verification-help-text">
                  Make sure you entered the correct email address during registration.
                </p>
                <button
                  onClick={handleEditEmail}
                  className="email-verification-update-email-btn"
                  type="button"
                >
                  <EditIcon />
                  Update email address
                </button>
              </div>
            </div>

            {/* Contact Support */}
            <div className="email-verification-support">
              <p className="email-verification-support-text">Still having trouble?</p>
              <button
                onClick={handleContactSupport}
                className="email-verification-support-btn"
                type="button"
              >
                <SupportIcon />
                Contact Support
              </button>
            </div>


          </div>
        </Form>
      </main>
    </div>
  );
};

ModernEmailVerification.propTypes = {
  onBack: PropTypes.func
};

export default ModernEmailVerification; 